<template>
  <div class="api-key-page">
    <section class="api-hero">
      <div>
        <div class="eyebrow"><app-icon name="key"></app-icon> API OPEN PLATFORM</div>
        <h2>API 开放平台</h2>
        <p>创建和管理 API Key，将{{ $siteConfig.siteName }}能力接入你的系统。</p>
      </div>
    </section>

    <div class="page-switch"><button :class="{ active: activePanel === 'keys' }" @click="switchPanel('keys')"><app-icon name="key"></app-icon> API Key 管理</button><button :class="{ active: activePanel === 'tasks' }" @click="switchPanel('tasks')"><app-icon name="history"></app-icon> 任务记录</button></div>

    <section v-if="activePanel === 'keys'" class="summary-grid">
      <div class="summary-card points-card">
        <div class="summary-icon"><app-icon name="bolt"></app-icon></div>
        <div>
          <div class="summary-label">当前可用点数</div>
          <div class="summary-value">{{ availablePoints }}</div>
          <p v-if="canCreateApiKey">已满足 API Key 创建条件</p>
          <p v-else>需大于 {{ createMinPoints }} 点才可创建新 Key</p>
        </div>
      </div>
      <div class="summary-card">
        <div class="summary-icon muted"><app-icon name="key"></app-icon></div>
        <div>
          <div class="summary-label">已创建 Key</div>
          <div class="summary-value">{{ items.length }} <small>/ {{ maxKeys }}</small></div>
          <p>最多保留 {{ maxKeys }} 个 API Key</p>
        </div>
      </div>
    </section>

    <section v-if="activePanel === 'keys'" class="content-card key-card">
      <div class="card-header">
        <div>
          <h3>API Key 管理</h3>
          <p>完整 Key 仅在创建时展示一次，请妥善保存。</p>
        </div>
        <div class="card-actions"><el-button size="small" icon="el-icon-refresh" :loading="loading" @click="loadKeys">刷新</el-button><el-button size="small" type="primary" icon="el-icon-plus" :disabled="!canCreate" @click="openCreate">创建 API Key</el-button></div>
      </div>

      <div v-if="items.length" class="key-table-wrap"><table class="key-table"><thead><tr><th>名称</th><th>Key</th><th>权限范围</th><th>创建日期</th><th>最近使用</th><th>操作</th></tr></thead><tbody><tr v-for="item in items" :key="item.id"><td><strong>{{ item.name }}</strong><span class="status-tag" :class="statusClass(item)">{{ statusText(item) }}</span></td><td><code>{{ item.key_prefix }}</code></td><td>{{ scopeText(item.scopes) }}</td><td>{{ formatTime(item.created_at) }}</td><td>{{ item.last_used_at ? formatTime(item.last_used_at) : '未使用' }}</td><td><div class="key-table-actions"><el-switch :value="item.status === 1" active-color="#1a73e8" @change="changeStatus(item, $event)"></el-switch><el-button type="text" size="mini" icon="el-icon-edit" title="编辑" @click="openEdit(item)"></el-button><el-button type="text" size="mini" icon="el-icon-delete" title="删除" class="delete-key-button" @click="deleteKey(item)"></el-button></div></td></tr></tbody></table></div>
      <div v-else class="empty-state">
        <app-icon name="key"></app-icon>
        <strong>还没有 API Key</strong>
        <p>创建一个 Key，即可为你的应用配置开放能力。</p>
        <el-button type="primary" size="small" :disabled="!canCreate" @click="openCreate">创建第一个 Key</el-button>
      </div>
    </section>

    <section v-if="activePanel === 'tasks'" class="content-card task-card">
      <div class="card-header">
        <div>
          <h3>API 任务记录</h3>
          <p>仅显示当前账户 API Key 创建的任务与成品结果。</p>
        </div>
        <el-button size="small" icon="el-icon-refresh" :loading="taskLoading" @click="loadTasks">刷新</el-button>
      </div>
      <div class="task-filters"><el-select v-model="taskFilters.apiKeyId" clearable placeholder="全部 API Key"><el-option v-for="item in items" :key="item.id" :label="item.name" :value="item.id"></el-option></el-select><el-select v-model="taskFilters.type" clearable placeholder="全部任务类型"><el-option label="视频去重 V1" value="qc"></el-option><el-option label="视频去重 V2" value="qcv2"></el-option><el-option label="视频混剪 V1" value="hjv1"></el-option><el-option label="视频混剪 V2" value="hjv2"></el-option></el-select><el-select v-model="taskFilters.status" clearable placeholder="全部状态"><el-option label="创建中" value="creating"></el-option><el-option label="已提交" value="submitted"></el-option><el-option label="处理中" value="processing"></el-option><el-option label="已完成" value="completed"></el-option><el-option label="失败" value="failed"></el-option></el-select><el-date-picker v-model="taskFilters.dateRange" type="daterange" value-format="timestamp" range-separator="至" start-placeholder="开始日期" end-placeholder="结束日期"></el-date-picker><el-input v-model.trim="taskFilters.keyword" clearable placeholder="搜索任务 ID" @keyup.enter.native="applyTaskFilters"></el-input><el-button type="primary" icon="el-icon-search" @click="applyTaskFilters">搜索</el-button><el-button @click="resetTaskFilters">重置</el-button></div>
      <div v-if="tasks.length" class="task-table-wrap">
        <table class="task-table">
          <thead><tr><th>任务类型</th><th>状态</th><th>点数</th><th>成品地址</th><th>创建时间</th></tr></thead>
          <tbody><tr v-for="task in tasks" :key="task.task_id"><td><strong>{{ taskTypeText(task.type) }}</strong><small>{{ task.api_key_name }}</small></td><td><span class="task-status" :class="'task-status-' + task.status">{{ taskStatusText(task.status) }}</span></td><td>{{ task.need_points }}</td><td><a v-if="task.result_urls && task.result_urls.length" :href="task.result_urls[0]" target="_blank" rel="noopener">查看成品<span v-if="task.result_urls.length > 1">（{{ task.result_urls.length }}）</span></a><span v-else class="task-empty">{{ task.status === 'failed' ? '暂无成品' : '处理中' }}</span></td><td>{{ formatDateTime(task.created_at) }}</td></tr></tbody>
        </table>
      </div>
      <div v-else class="task-empty-state">暂无 API 任务记录</div>
      <el-pagination v-if="taskTotal > taskPageSize" class="task-pagination" layout="prev, pager, next" :current-page="taskPage" :page-size="taskPageSize" :total="taskTotal" @current-change="changeTaskPage"></el-pagination>
    </section>

    <section v-if="activePanel === 'keys'" class="content-card docs-card">
      <div class="card-header">
        <div>
          <h3>调用说明</h3>
          <p>支持视频去重与混剪，通过同一接口使用 type 区分任务类型。</p>
        </div>
        <el-button size="small" type="primary" plain @click="openApiDocs">接口文档与调试</el-button>
      </div>
      <div class="code-box">
        <div class="code-title">认证请求头</div>
        <pre>QHY-API-Key: qhy_sk_xxxxxxxxxxxxxxxxx</pre>
      </div>
      <p class="docs-note">POST /api/v1/open/video/tasks，type 支持 qc、qcv2、hjv1、hjv2；需传 Idempotency-Key 防止重复提交。</p>
    </section>

    <el-dialog :title="editingId ? '编辑 API Key' : '创建 API Key'" :visible.sync="dialogVisible" :width="formDialogWidth"
      :close-on-click-modal="false" custom-class="api-key-dialog">
      <el-form label-position="top">
        <el-form-item label="Key 名称">
          <el-input v-model.trim="form.name" maxlength="50" show-word-limit placeholder="例如：我的自动剪辑服务"></el-input>
        </el-form-item>
        <el-form-item label="权限范围">
          <el-checkbox v-model="allScopes" @change="onAllScopesChange">全部可用（包含后续新增功能）</el-checkbox>
          <el-checkbox-group v-model="form.scopes" :disabled="allScopes" class="scope-list">
            <el-checkbox label="video_dedup">视频去重（V1 / V2）</el-checkbox>
            <el-checkbox label="video_mix">视频混剪（V1 / V2）</el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="有效期">
          <el-radio-group v-model="form.expiresDays">
            <el-radio :label="30">30 天</el-radio>
            <el-radio :label="90">90 天</el-radio>
            <el-radio :label="365">1 年</el-radio>
            <el-radio :label="0">永久</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div v-if="!editingId && !canCreateApiKey" class="point-warning">
        当前可用点数 {{ availablePoints }}，需大于 {{ createMinPoints }} 点才可创建。
      </div>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" :disabled="!editingId && !canCreate" @click="saveKey">
          {{ editingId ? '保存修改' : '立即创建' }}
        </el-button>
      </div>
    </el-dialog>

    <el-dialog title="请立即保存 API Key" :visible.sync="secretVisible" :width="secretDialogWidth" :close-on-click-modal="false"
      :close-on-press-escape="false" custom-class="api-key-dialog secret-dialog">
      <div class="secret-tip"><app-icon name="info"></app-icon> 完整 Key 只会展示这一次，关闭后无法再次查看。</div>
      <div class="secret-value"><code>{{ newSecret }}</code><el-button size="mini" @click="copySecret">复制</el-button></div>
      <div slot="footer"><el-button type="primary" @click="secretVisible = false">我已保存</el-button></div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'User2ApiKey',
  data() {
    return {
      loading: false,
      saving: false,
      isMobile: window.innerWidth <= 768,
      activePanel: 'keys',
      items: [],
      availablePoints: 0,
      canCreateApiKey: false,
      createMinPoints: 10000,
      maxKeys: 5,
      taskLoading: false,
      tasks: [],
      taskTotal: 0,
      taskPage: 1,
      taskPageSize: 10,
      taskFilters: { apiKeyId: '', type: '', status: '', keyword: '', dateRange: [] },
      dialogVisible: false,
      secretVisible: false,
      newSecret: '',
      editingId: null,
      allScopes: true,
      form: { name: '', scopes: ['*'], expiresDays: 0 }
    }
  },
  computed: {
    canCreate() {
      return this.canCreateApiKey && this.items.length < this.maxKeys
    },
    formDialogWidth() {
      return this.isMobile ? 'calc(100% - 24px)' : '480px'
    },
    secretDialogWidth() {
      return this.isMobile ? 'calc(100% - 24px)' : '520px'
    }
  },
  methods: {
    openApiDocs() {
      window.open(this.$router.resolve({ path: '/api-docs' }).href, '_blank', 'noopener')
    },
    updateMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    async loadKeys() {
      this.loading = true
      try {
        const res = await this.$ajax.get('api/v1/users/api-keys')
        const body = res.data || {}
        if (body.status !== 200) throw new Error(body.msg || '加载失败')
        const data = body.data || {}
        this.items = data.items || []
        this.availablePoints = Number(data.available_points || 0)
        this.canCreateApiKey = Boolean(data.can_create)
        this.createMinPoints = Number(data.create_min_points || 10000)
        this.maxKeys = Number(data.max_keys || 5)
      } catch (err) {
        this.$message.error(this.errorMessage(err, '加载 API Key 失败'))
      } finally {
        this.loading = false
      }
    },
    async loadTasks() {
      this.taskLoading = true
      try {
        const params = { page: this.taskPage, page_size: this.taskPageSize, api_key_id: this.taskFilters.apiKeyId || undefined, type: this.taskFilters.type || undefined, status: this.taskFilters.status || undefined, keyword: this.taskFilters.keyword || undefined }
        if (this.taskFilters.dateRange && this.taskFilters.dateRange.length === 2) {
          params.start_at = Math.floor(this.taskFilters.dateRange[0] / 1000)
          params.end_at = Math.floor((this.taskFilters.dateRange[1] + 86400000 - 1) / 1000)
        }
        const res = await this.$ajax.get('api/v1/users/api-keys/tasks', { params })
        const body = res.data || {}
        if (body.status !== 200) throw new Error(body.msg || '加载失败')
        const data = body.data || {}
        this.tasks = data.items || []
        this.taskTotal = Number(data.total || 0)
      } catch (err) {
        this.$message.error(this.errorMessage(err, '加载任务记录失败'))
      } finally {
        this.taskLoading = false
      }
    },
    changeTaskPage(page) {
      this.taskPage = page
      this.loadTasks()
    },
    switchPanel(panel) {
      this.activePanel = panel
      if (panel === 'tasks') this.loadTasks()
    },
    applyTaskFilters() {
      this.taskPage = 1
      this.loadTasks()
    },
    resetTaskFilters() {
      this.taskFilters = { apiKeyId: '', type: '', status: '', keyword: '', dateRange: [] }
      this.applyTaskFilters()
    },
    openCreate() {
      if (!this.canCreate) {
        this.$message.warning(`可用点数需大于 ${this.createMinPoints}，且最多保留 ${this.maxKeys} 个 Key`)
        return
      }
      this.editingId = null
      this.allScopes = true
      this.form = { name: '', scopes: ['*'], expiresDays: 0 }
      this.dialogVisible = true
    },
    openEdit(item) {
      this.editingId = item.id
      this.allScopes = (item.scopes || []).includes('*')
      this.form = {
        name: item.name,
        scopes: this.allScopes ? ['*'] : (item.scopes || ['video_dedup']),
        expiresDays: this.daysFromExpiry(item.expires_at)
      }
      this.dialogVisible = true
    },
    onAllScopesChange(value) {
      this.form.scopes = value ? ['*'] : ['video_dedup', 'video_mix']
    },
    async saveKey() {
      if (!this.form.name) {
        this.$message.warning('请输入 Key 名称')
        return
      }
      this.saving = true
      const payload = {
        name: this.form.name,
        scopes: this.allScopes ? ['*'] : this.form.scopes,
        expires_days: this.form.expiresDays
      }
      try {
        let res
        if (this.editingId) res = await this.$ajax.patch(`api/v1/users/api-keys/${this.editingId}`, payload)
        else res = await this.$ajax.post('api/v1/users/api-keys', payload)
        const body = res.data || {}
        if (body.status !== 200) throw new Error(body.msg || '保存失败')
        this.dialogVisible = false
        if (!this.editingId && body.data && body.data.api_key) {
          this.newSecret = body.data.api_key
          this.secretVisible = true
        }
        this.$message.success(body.msg || '保存成功')
        this.loadKeys()
      } catch (err) {
        this.$message.error(this.errorMessage(err, '保存失败'))
      } finally {
        this.saving = false
      }
    },
    async changeStatus(item, enabled) {
      try {
        const res = await this.$ajax.patch(`api/v1/users/api-keys/${item.id}`, { status: enabled ? 1 : 0 })
        const body = res.data || {}
        if (body.status !== 200) throw new Error(body.msg || '操作失败')
        item.status = enabled ? 1 : 0
        this.$message.success(enabled ? '已启用' : '已停用')
      } catch (err) {
        this.$message.error(this.errorMessage(err, '操作失败'))
      }
    },
    deleteKey(item) {
      this.$confirm(`删除“${item.name}”后不可恢复，是否继续？`, '删除 API Key', { type: 'warning' }).then(async () => {
        try {
          const res = await this.$ajax.delete(`api/v1/users/api-keys/${item.id}`)
          const body = res.data || {}
          if (body.status !== 200) throw new Error(body.msg || '删除失败')
          this.$message.success('已删除')
          this.loadKeys()
        } catch (err) {
          this.$message.error(this.errorMessage(err, '删除失败'))
        }
      }).catch(() => {})
    },
    copySecret() {
      const input = document.createElement('textarea')
      input.value = this.newSecret
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      this.$message.success('已复制')
    },
    statusText(item) {
      if (item.status !== 1) return '已停用'
      if (item.expires_at && item.expires_at <= Math.floor(Date.now() / 1000)) return '已过期'
      return '正常'
    },
    statusClass(item) {
      const text = this.statusText(item)
      return text === '正常' ? 'status-normal' : (text === '已过期' ? 'status-expired' : 'status-disabled')
    },
    scopeText(scopes) {
      if ((scopes || []).includes('*')) return '全部功能（含后续新增）'
      const labels = { video_dedup: '视频去重', video_mix: '视频混剪' }
      return (scopes || []).map(scope => labels[scope] || scope).join('、')
    },
    expireText(timestamp) {
      if (!timestamp) return '永久有效'
      return `有效至 ${this.formatTime(timestamp)}`
    },
    daysFromExpiry(timestamp) {
      if (!timestamp) return 0
      const days = Math.ceil((timestamp - Math.floor(Date.now() / 1000)) / 86400)
      if (days <= 30) return 30
      if (days <= 90) return 90
      return 365
    },
    formatTime(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp * 1000)
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    },
    formatDateTime(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp * 1000)
      return `${this.formatTime(timestamp)} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    },
    taskTypeText(type) {
      return { qc: '视频去重 V1', qcv2: '视频去重 V2', hjv1: '视频混剪 V1', hjv2: '视频混剪 V2' }[type] || type
    },
    taskStatusText(status) {
      return { creating: '创建中', submitted: '已提交', processing: '处理中', completed: '已完成', failed: '失败' }[status] || status
    },
    errorMessage(err, fallback) {
      return (err && err.response && err.response.data && err.response.data.msg) || (err && err.message) || fallback
    }
  },
  mounted() {
    window.addEventListener('resize', this.updateMobile)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateMobile)
  },
  created() {
    this.loadKeys()
  }
}
</script>

<style scoped>
/* ===== 页面容器 ===== */
.api-key-page {
  box-sizing: border-box;
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: 24px 28px 40px;
  color: #1e293b;
}

/* ===== 页头 ===== */
.api-hero {
  padding: 2px 0 20px;
}

.eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #1a73e8;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .06em;
}

.eyebrow .app-icon {
  font-size: 14px;
}

.api-hero h2 {
  margin: 10px 0 6px;
  color: #0f172a;
  font-size: 24px;
  line-height: 1.35;
}

.api-hero p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
}

/* ===== 视图切换：下划线式，比胶囊容器更简洁 ===== */
.page-switch {
  display: flex;
  gap: 26px;
  margin-bottom: 22px;
  border-bottom: 1px solid #e8edf4;
}

.page-switch button {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 2px 12px;
  border: 0;
  background: transparent;
  color: #64748b;
  font-size: 14px;
  cursor: pointer;
  transition: color .2s;
}

.page-switch button:hover {
  color: #1a73e8;
}

.page-switch button.active {
  color: #1a73e8;
  font-weight: 600;
}

.page-switch button.active::after {
  content: '';
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  height: 2px;
  border-radius: 2px;
  background: #1a73e8;
}

/* ===== 概览卡片 ===== */
.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 20px;
}

.summary-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  border: 1px solid #e8edf4;
  border-radius: 12px;
  background: #fff;
}

.points-card {
  border-color: #d9e6fb;
}

.summary-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #eef4ff;
  color: #1a73e8;
  font-size: 19px;
}

.summary-icon.muted {
  background: #f4f6fa;
  color: #94a3b8;
}

.summary-label {
  color: #64748b;
  font-size: 13px;
}

.summary-value {
  margin-top: 2px;
  color: #0f172a;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
}

.summary-value small {
  color: #94a3b8;
  font-size: 14px;
  font-weight: 500;
}

.summary-card p {
  margin: 5px 0 0;
  color: #94a3b8;
  font-size: 12px;
}

/* ===== 内容卡片 ===== */
.content-card {
  margin-bottom: 20px;
  padding: 22px;
  border: 1px solid #e8edf4;
  border-radius: 12px;
  background: #fff;
}

.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.card-header h3 {
  margin: 0 0 5px;
  color: #0f172a;
  font-size: 16px;
}

.card-header p {
  margin: 0;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.6;
}

.card-actions {
  display: flex;
  flex-shrink: 0;
  gap: 8px;
}

/* ===== 数据表格：API Key 与任务记录共用一套 ===== */
.key-table-wrap,
.task-table-wrap {
  overflow-x: auto;
  border: 1px solid #e8edf4;
  border-radius: 10px;
}

.key-table,
.task-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.key-table {
  min-width: 860px;
}

.task-table {
  min-width: 760px;
}

.key-table th,
.key-table td,
.task-table th,
.task-table td {
  padding: 13px 16px;
  border-bottom: 1px solid #f1f4f9;
  text-align: left;
  vertical-align: middle;
}

.key-table th,
.task-table th {
  background: #fafbfd;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
}

.key-table tbody tr:last-child td,
.task-table tbody tr:last-child td {
  border-bottom: 0;
}

.key-table tbody tr:hover td,
.task-table tbody tr:hover td {
  background: #fafcff;
}

.key-table td,
.task-table td {
  color: #334155;
}

.key-table td strong {
  margin-right: 7px;
  color: #0f172a;
}

.key-table td code {
  padding: 3px 7px;
  border-radius: 5px;
  background: #f4f6fa;
  color: #475569;
  font-size: 12px;
  white-space: nowrap;
}

.key-table-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
}

.key-table-actions .el-button {
  padding: 5px;
  color: #94a3b8;
  font-size: 16px;
}

.key-table-actions .el-button:hover {
  color: #1a73e8;
}

.key-table-actions .el-switch {
  margin-right: 4px;
}

/* ===== 状态标签 ===== */
.status-tag,
.task-status {
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
}

.task-status {
  padding: 3px 8px;
  font-size: 12px;
  font-weight: 500;
}

.status-normal,
.task-status-completed {
  color: #047857;
  background: #ecfdf5;
}

.status-disabled {
  color: #64748b;
  background: #f1f5f9;
}

.status-expired {
  color: #b45309;
  background: #fffbeb;
}

.task-status-failed {
  color: #b91c1c;
  background: #fef2f2;
}

.task-status-creating,
.task-status-submitted,
.task-status-processing {
  color: #1d4ed8;
  background: #eff6ff;
}

/* ===== 空状态 ===== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 52px 18px 40px;
  color: #64748b;
  text-align: center;
}

.empty-state > .app-icon {
  margin-bottom: 14px;
  color: #bfd4f5;
  font-size: 36px;
}

.empty-state strong {
  color: #334155;
  font-size: 15px;
}

.empty-state p {
  margin: 8px 0 18px;
  font-size: 13px;
}

.task-empty-state {
  padding: 40px 12px;
  color: #94a3b8;
  font-size: 13px;
  text-align: center;
}

.task-empty {
  color: #94a3b8;
}

/* ===== 任务筛选 ===== */
.task-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}

.task-filters .el-select {
  width: 150px;
}

.task-filters .el-input {
  width: 190px;
}

/* ===== 任务表格补充 ===== */
.task-table td strong,
.task-table td small {
  display: block;
}

.task-table td strong {
  color: #0f172a;
}

.task-table td small {
  margin-top: 4px;
  color: #94a3b8;
  font-size: 11px;
}

.task-table a {
  color: #1a73e8;
  text-decoration: none;
}

.task-table a:hover {
  text-decoration: underline;
}

.task-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

/* ===== 调用说明 ===== */
.code-box {
  overflow: hidden;
  border: 1px solid #e8edf4;
  border-radius: 10px;
  background: #fafbfd;
}

.code-title {
  padding: 9px 14px;
  border-bottom: 1px solid #e8edf4;
  color: #64748b;
  font-size: 12px;
}

.code-box pre {
  margin: 0;
  padding: 14px;
  color: #334155;
  font-size: 13px;
  white-space: pre-wrap;
}

.docs-note {
  margin: 12px 0 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.7;
}

/* ===== 弹窗内元素 ===== */
.scope-list {
  display: block;
  margin-top: 6px;
}

.point-warning,
.secret-tip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fffbeb;
  color: #b45309;
  font-size: 13px;
  line-height: 1.6;
}

.secret-tip {
  margin-bottom: 14px;
}

.secret-value {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f4f6fa;
}

.secret-value code {
  flex: 1;
  overflow: hidden;
  color: #0f172a;
  font-size: 12px;
  word-break: break-all;
}

/* ===== 响应式 ===== */
@media (max-width: 768px) {
  .api-key-page {
    padding: 18px 14px 28px;
  }

  .api-hero {
    padding: 0 0 18px;
  }

  .api-hero h2 {
    margin-top: 8px;
    font-size: 21px;
  }

  .api-hero p {
    line-height: 1.6;
  }

  .page-switch {
    gap: 0;
    margin-bottom: 16px;
  }

  .page-switch button {
    flex: 1;
    justify-content: center;
  }

  .summary-grid {
    grid-template-columns: 1fr;
    gap: 10px;
    margin-bottom: 14px;
  }

  .summary-card {
    padding: 15px 16px;
  }

  .content-card {
    margin-bottom: 14px;
    padding: 16px;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .card-actions {
    width: 100%;
  }

  .card-actions .el-button {
    flex: 1;
  }

  .key-table-wrap,
  .task-table-wrap {
    margin: 0 -2px;
  }

  .key-table th,
  .key-table td,
  .task-table th,
  .task-table td {
    padding: 12px 14px;
  }

  .key-table-actions {
    justify-content: flex-start;
  }

  .task-filters {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .task-filters .el-select,
  .task-filters .el-input,
  .task-filters .el-date-editor,
  .task-filters .el-button {
    width: 100%;
    margin-left: 0;
  }

  .code-box {
    overflow-x: auto;
  }

  .code-box pre {
    min-width: max-content;
  }

  .scope-list .el-checkbox {
    display: block;
    margin: 8px 0 0;
  }

  .secret-value {
    flex-direction: column;
    align-items: stretch;
  }

  .secret-value .el-button {
    align-self: flex-end;
  }

  .point-warning {
    align-items: flex-start;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/User2/ApiKey.vue